// Eskom PARCC — CES grey header-field mapping (R3 #1).
//
// The working-paper templates carry a `headerFields` array — the grey header
// row of the printed working paper. This maps those labels onto the
// transaction: the bulk-population fields (Division, BU, Brief Description,
// Supplier, Contract/PO Number, Value, Buyer) render LOCKED; the four
// OpenText-sourced fields (CSD Number, PR Number, Procurement Manager,
// End-User) render EDITABLE and persist onto the transaction at save. Labels
// with no mapping (e.g. "Approver of the Urgent Request Form", "Procurement
// Practitioner") are skipped — they exist on paper but have no system column.
import type { Transaction } from '../../data/types';
import { formatRand } from '../../lib/money';

/** The four reviewer-entered (OpenText-sourced) header values. */
export interface EditableHeaderValues {
  csdNumber: string;
  prNumber: string;
  procurementManager: string;
  endUser: string;
}
export type EditableHeaderKey = keyof EditableHeaderValues;

/** One rendered header field: a locked value or an editable input binding. */
export interface CesHeaderField {
  label: string;
  value: string;
  editable: boolean;
  /** Set only for editable fields — the EditableHeaderValues key to bind. */
  key?: EditableHeaderKey;
}

/** The canonical grey header set, used when no template resolves. */
export const CANONICAL_HEADER_FIELDS: readonly string[] = [
  'Division',
  'BU',
  'Brief Description',
  'Supplier Name/Vendor Number',
  'CSD Number',
  'PR number',
  'Contract/PO Number',
  'Value of Contract/PO',
  'Buyer / Division',
  'Procurement Manager',
  'End-User',
];

/** Canonicalise a header label for matching (template spellings vary). Shared
 *  with the workbook-upload parser/matcher (R5 #1) so header keys stay in
 *  lock-step with this module's switch labels. */
export function normHeaderLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}
const norm = normHeaderLabel;

/**
 * Map a template's header labels onto `{ label, value, editable }` rows, in
 * template order. Locked values read from the transaction (BU falls back to the
 * upload's OU column; Contract/PO Number to the reference); editable values
 * come from the page's working copy. Unknown labels are skipped.
 */
export function cesHeaderFields(
  headerFields: readonly string[] | undefined,
  tx: Transaction,
  edited: EditableHeaderValues,
): CesHeaderField[] {
  const labels = headerFields && headerFields.length > 0 ? headerFields : CANONICAL_HEADER_FIELDS;
  const out: CesHeaderField[] = [];
  for (const label of labels) {
    const n = norm(label);
    switch (n) {
      case 'division':
        out.push({ label, value: tx.division ?? '', editable: false });
        break;
      case 'bu':
        out.push({ label, value: tx.bu ?? tx.ou ?? '', editable: false });
        break;
      case 'brief description':
        out.push({ label, value: tx.briefDescription ?? '', editable: false });
        break;
      case 'supplier name vendor number':
        out.push({ label, value: tx.supplier ?? '', editable: false });
        break;
      case 'contract po number':
        out.push({ label, value: tx.contractNumber ?? tx.referenceNo, editable: false });
        break;
      case 'value of contract po':
        out.push({
          label,
          value: tx.value ? formatRand(tx.value) : (tx.valueDisplay ?? ''),
          editable: false,
        });
        break;
      case 'buyer division':
      case 'buyer':
        out.push({ label, value: tx.buyer ?? '', editable: false });
        break;
      case 'link':
      case 'links':
        out.push({ label, value: tx.link ?? '', editable: false });
        break;
      case 'csd number':
        out.push({ label, value: edited.csdNumber, editable: true, key: 'csdNumber' });
        break;
      case 'pr number':
        out.push({ label, value: edited.prNumber, editable: true, key: 'prNumber' });
        break;
      case 'procurement manager':
        out.push({ label, value: edited.procurementManager, editable: true, key: 'procurementManager' });
        break;
      case 'end user':
        out.push({ label, value: edited.endUser, editable: true, key: 'endUser' });
        break;
      default:
        // Paper-only label (no system column) — skipped by design.
        break;
    }
  }
  return out;
}
